import moment, { lang } from './calendarMoment.js'
import wishlistStore from './wishlistStore.js'

// ── Booking element ─────────────────────────────────────────────────────────
// Mirrors of Glunz\EventDate\Repository — the three surfaces that render a
// date's booking element (mini calendar, wishlist, event detail in PHP) must
// agree, so the rules live in ONE place per language instead of one copy per
// consumer. Labels are passed in from the caller's data-i18n; nothing here
// translates.

// Repository::bookingUrl() — a bare email becomes a `mailto:` link; anything
// that already carries a URI scheme (http:, mailto:, tel:) is left as-is.
export const bookingUrl = (url) => {
  url = (url || '').trim()
  if (!url) return ''
  if (url.includes('@') && !/^[a-z][a-z0-9+.-]*:/i.test(url)) return `mailto:${url}`
  return url
}

// Repository::ticketLabel() — the editor's explicit label wins; otherwise a
// label derived from the registration type, so a date reads sensibly even
// without a link. '' for `none` with no explicit label.
export const ticketLabel = (title, registrationType, labels) => {
  title = (title || '').trim()
  if (title) return title
  switch (registrationType) {
    case 'no_registration_required': return labels.noRegistration || ''
    case 'ticket':                   return labels.tickets || ''
    case 'registration':             return labels.registration || ''
    case 'info':                     return labels.info || ''
    case 'tickets_soon':             return labels.ticketsSoon || ''
    case 'registration_soon':        return labels.registrationSoon || ''
    default:                         return ''
  }
}

// Repository::ANNOUNCED_TYPES — a sale/registration that has been announced but
// has not opened yet renders as plain text and never links, even if the editor
// has already pasted the url.
const ANNOUNCED_TYPES = new Set(['tickets_soon', 'registration_soon'])

// Repository::booking() — resolves { href, label, external } for one date.
// `info` falls back to the event permalink, which is same-origin and therefore
// NOT external. See the PHP docblock for the full reasoning.
export const resolveBooking = (row, labels, fallbackUrl = '') => {
  const registrationType = row.registrationType ?? row.registration_type ?? ''
  const label = ticketLabel(
    row.ticketTitle ?? row.ticket_title,
    registrationType,
    labels,
  )

  if (ANNOUNCED_TYPES.has(registrationType)) return { href: '', label, external: false }

  const href = bookingUrl(row.ticketUrl ?? row.ticket_url)

  if (registrationType === 'info' && !href) {
    const fallback = (fallbackUrl || '').trim()
    if (!fallback) return { href: '', label: '', external: false }
    return { href: fallback, label, external: false }
  }

  return { href, label, external: !!href && !href.startsWith('mailto:') }
}

export const element = (tagName, classNames, attributes = {}) => {
  const el = document.createElement(tagName)
  if (classNames) el.className = classNames
  for (const [key, value] of Object.entries(attributes)) {
    el.setAttribute(key, value)
  }
  return el
}

const toArray = (input) => Array.isArray(input) ? input : (input?.dates ?? [])

const eventsOnDay = (eventDates, day) => {
  const key = day.format('YYYY-MM-DD')
  return toArray(eventDates).filter(e => (e.date_start || '').startsWith(key))
}

export const parseArtistNames = (raw) => {
  if (!raw || typeof raw !== 'string') return []
  return raw.split('|').map(s => s.trim()).filter(Boolean)
}

// Display title for an event row within a single day's list. When the same
// event puts more than one entry on that day — most often the different
// semesters of a course landing on the same weekday — the plain post title
// repeats and the rows are indistinguishable. Suffix each colliding semester
// row with its semester title so they read e.g. "Ballett – Frühling 26" /
// "Ballett – Herbst 26". Only semester rows carry a semester_title, and we only
// suffix on an actual same-day collision for that event.
export const eventDisplayTitle = (event, dayEvents = []) => {
  const title = event.post_title || ''
  const semesterTitle = (event.semester_title || '').trim()
  if (!semesterTitle) return title
  const sameEventCount = dayEvents.filter(e => String(e.post_id) === String(event.post_id)).length
  return sameEventCount > 1 ? `${title} – ${semesterTitle}` : title
}

// An event carries the "Veranstaltung" event-type when its pipe-wrapped
// event_types slug string contains one of that concept's slugs. `veranstaltungen`
// is the current German slug, `veranstaltung` the older one kept as a fallback
// (works before a resync / on an env still on it), and `shows` is the English
// term — WPML rows carry the slug of their own language, so the English calendar
// never sees the German one. Same cross-env/-language slug-list rationale as
// calendar.js. Mirrors the `slugs.some` match calendarOverlay.js uses.
const VERANSTALTUNG_SLUGS = ['veranstaltungen', 'veranstaltung', 'shows']
const isVeranstaltung = (event) =>
  VERANSTALTUNG_SLUGS.some(s => (event.event_types || '').includes(`|${s}|`))

// Turn the pipe-wrapped taxonomy slug strings (event_types / event_skills /
// event_audiences / event_tags, e.g. "|veranstaltung|kurse|") into class names on
// the event <li>: "event-type-veranstaltungen event-skill-profis tag-profitraining".
// The first three are the match targets the calendar's client-side filter uses
// (calendar.js applyFilters), so every filterable taxonomy needs its slugs here.
// These slugs are per-language, so they are NOT good styling hooks — style the
// language-agnostic markers instead (`.is-veranstaltung`). event_tags is a styling
// hook (not a calendar filter) and uses the shorter `tag-` prefix; its tags are
// untranslated, so keying CSS on them is safe.
const TAXONOMY_CLASS_FIELDS = [
  { field: 'event_types', prefix: 'event-type' },
  { field: 'event_skills', prefix: 'event-skill' },
  { field: 'event_audiences', prefix: 'event-audience' },
  { field: 'event_tags', prefix: 'tag' },
]
const eventTaxonomyClasses = (event) =>
  TAXONOMY_CLASS_FIELDS
    .flatMap(({ field, prefix }) =>
      (event[field] || '').split('|').filter(Boolean).map(slug => `${prefix}-${slug}`))
    .join(' ')

// Set an element's text via an inner <span class="for-bg"> instead of plain
// textContent — a styling hook (per-line-box background) used across the
// calendar event teaser.
const setText = (el, text) => {
  const span = element('span', 'for-bg')
  span.textContent = text
  el.appendChild(span)
}

// ── Wishlist ("Merkliste") heart ────────────────────────────────────────────
// DB tinyint columns arrive as "0"/"1" strings over REST.
const isTrue = (v) => v === 1 || v === '1' || v === true

const wishlistStatus = (row) =>
  isTrue(row.is_cancelled) ? 'cancelled' : (isTrue(row.is_sold_out) ? 'sold_out' : 'available')

// The snapshot cached in localStorage on heart-click: enough raw fields to render
// the teaser AND rebuild the .ics, plus a resolved title. Reconciled against live
// data later (wishlist.js) so cancelled/sold-out/moved states stay correct.
const buildWishlistSnapshot = (event, dayEvents) => ({
  key: wishlistStore.keyFor(event),
  postId: event.post_id,
  dateStart: event.date_start,
  dateEnd: event.date_end || null,
  postTitle: event.post_title || '',
  semesterTitle: event.semester_title || '',
  title: eventDisplayTitle(event, dayEvents),
  artists: parseArtistNames(event.artist_names).join(', '),
  permalink: event.permalink || '',
  registrationType: event.registration_type || '',
  ticketTitle: event.ticket_title || '',
  ticketUrl: event.ticket_url || '',
  isCancelled: event.is_cancelled,
  isSoldOut: event.is_sold_out,
  soldOutText: event.sold_out_text || '',
  soldOutType: event.sold_out_type || '',
  status: wishlistStatus(event),
})

const wishlistLabel = (saved) => saved
  ? (lang === 'de' ? 'Von der Wunschliste entfernen' : 'Remove from wishlist')
  : (lang === 'de' ? 'Zur Wunschliste hinzufügen' : 'Add to wishlist')

// A heart toggle pinned to the time line of an event cell (styled in
// 65_wishlist.sass). Sits OUTSIDE the <a> (a button inside an anchor is invalid
// and would navigate) as a sibling of the link, absolutely positioned. Self-
// initialises its saved state so lazily-appended weeks / prepended past months are
// correct; wishlist.js re-syncs it when the store changes elsewhere (e.g. an item
// removed from the panel).
const renderWishlistHeart = (event, dayEvents) => {
  const key = wishlistStore.keyFor(event)
  const saved = wishlistStore.has(key)
  const btn = element('button', `calendar-event-wishlist${saved ? ' is-wishlisted' : ''}`, {
    type: 'button',
    'data-wishlist-key': key,
    'aria-pressed': String(saved),
    'aria-label': wishlistLabel(saved),
  })
  // The glyph itself comes from a ::before in 65_wishlist.sass (♡ vs ♥ per state),
  // so the button stays empty and is named by its aria-label.
  btn.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    const now = wishlistStore.toggle(buildWishlistSnapshot(event, dayEvents))
    btn.classList.toggle('is-wishlisted', now)
    btn.setAttribute('aria-pressed', String(now))
    btn.setAttribute('aria-label', wishlistLabel(now))
    // Adding (not removing) slides the panel open so the visitor sees the item land.
    // wishlist.js listens; a DOM event keeps this module free of a panel import.
    if (now) document.dispatchEvent(new CustomEvent('wishlist:added'))
  })
  return btn
}

// Build one <li> for an event. Extracted from renderDay so a day can render its
// two ordered groups (regular events, then "veranstaltung" events) the same way.
const renderEventItem = (event, now, dayEvents = []) => {
  const start = moment(event.date_start)
  const end = event.date_end ? moment(event.date_end) : null
  const isPast = (end || start).isBefore(now)

  // `.is-veranstaltung` is the language-agnostic styling hook for the
  // Veranstaltung/Show group (magenta marker, solid-fill hover): the per-language
  // event-type slug classes still ride along for the filter, but the CSS keys on
  // this one class instead of enumerating `event-type-veranstaltungen`,
  // `event-type-shows`, … for every language.
  const classes = [
    'calendar-event',
    'fs-small',
    isPast ? 'event-past' : '',
    isVeranstaltung(event) ? 'is-veranstaltung' : '',
    eventTaxonomyClasses(event),
  ].filter(Boolean).join(' ')
  const item = element('li', classes, {
    'data-event-date-id': event.id,
    'data-post-id': event.post_id,
    'data-cancelled': event.is_cancelled,
    'data-sold-out': event.is_sold_out,
  })

  // Make the whole event a link to its detail page. Falls back to a plain
  // <div> if the payload carries no permalink, so the cell still renders.
  const link = element(
    event.permalink ? 'a' : 'div',
    'calendar-event-link',
    event.permalink ? { href: event.permalink } : {},
  )

  const time = element('span', 'calendar-event-time')
  setText(time, end && !end.isSame(start)
    ? `${start.format('HH:mm')} – ${end.format('HH:mm')}`
    : start.format('HH:mm'))
  link.appendChild(time)

  const title = element('span', 'calendar-event-title')
  setText(title, eventDisplayTitle(event, dayEvents))
  link.appendChild(title)

  const artists = parseArtistNames(event.artist_names)
  if (artists.length) {
    const ul = element('ul', 'calendar-event-artists')
    for (const name of artists) {
      const li = element('li', 'calendar-event-artist')
      setText(li, name)
      ul.appendChild(li)
    }
    link.appendChild(ul)
  }

  item.appendChild(link)
  item.appendChild(renderWishlistHeart(event, dayEvents))
  return item
}

export const renderDay = (date, currentMonth, eventDates) => {
  const day = moment.isMoment(date) ? date.clone() : moment(date)
  const isOutside = typeof currentMonth === 'number' && day.month() !== currentMonth
  const now = moment()
  const isToday = day.isSame(now, 'day')
  // Days before today render grayed (`.is-past`) — the past days inside the
  // current week, and every day of a past month pulled in via the ← arrow.
  const isPast = day.isBefore(now, 'day')

  const cell = element('div', `calendar-day${isOutside ? ' is-outside' : ''}${isToday ? ' is-today' : ''}${isPast ? ' is-past' : ''}`, {
    'data-date': day.format('YYYY-MM-DD'),
  })

  const number = element('span', 'calendar-day-number')
  number.textContent = day.format('D')
  cell.appendChild(number)

  // Events arrive sorted by date_start. Render the regular events first, then —
  // separated by a divider — the "veranstaltung"-tagged ones, so they always
  // come last within the day. The divider only appears when both groups exist.
  const list = element('ul', 'calendar-day-events')
  const dayEvents = eventsOnDay(eventDates, day)
  const regular = dayEvents.filter(e => !isVeranstaltung(e))
  const veranstaltungen = dayEvents.filter(isVeranstaltung)

  for (const event of regular) list.appendChild(renderEventItem(event, now, dayEvents))
  if (regular.length && veranstaltungen.length) {
    list.appendChild(element('li', 'calendar-day-divider', { 'aria-hidden': 'true' }))
  }
  for (const event of veranstaltungen) list.appendChild(renderEventItem(event, now, dayEvents))

  cell.appendChild(list)

  return cell
}

export const renderWeekRow = (start, currentMonth, eventDates) => {
  const row = element('div', 'calendar-week', { 'data-iso-week': start.isoWeek() })
  for (let i = 0; i < 7; i++) {
    row.appendChild(renderDay(start.clone().add(i, 'days'), currentMonth, eventDates))
  }
  return row
}

export const renderWeekdayHeader = (anchor) => {
  const head = element('div', 'calendar-weekdays')
  const monday = anchor.clone().startOf('isoWeek')
  for (let i = 0; i < 7; i++) {
    const c = element('div', 'calendar-weekday')
    c.textContent = monday.clone().add(i, 'days').format('dddd')
    head.appendChild(c)
  }
  return head
}

export const renderWeek = (year, week, eventDates) => {
  const start = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek')
  const wrapper = element('div', 'calendar-view-week', { 'data-year': year, 'data-week': week })
  wrapper.appendChild(renderWeekdayHeader(start))
  wrapper.appendChild(renderWeekRow(start, undefined, eventDates))
  return wrapper
}

export const renderMonth = (year, month, eventDates) => {
  const first = moment({ year, month, day: 1 })
  const gridStart = first.clone().startOf('isoWeek')
  const gridEnd = first.clone().endOf('month').endOf('isoWeek')

  const wrapper = element('div', 'calendar-view-month', {
    'data-year': year,
    'data-month': month + 1,
  })
  wrapper.appendChild(renderWeekdayHeader(first))

  const cursor = gridStart.clone()
  while (cursor.isSameOrBefore(gridEnd, 'day')) {
    wrapper.appendChild(renderWeekRow(cursor.clone(), month, eventDates))
    cursor.add(7, 'days')
  }
  return wrapper
}
